import { useMemo } from 'react';
import * as Yup from 'yup';

import {
  addressExcludeNYSchema,
  // Billing address allows NY
  addressSchema as billingAddressSchema,
} from '@validators/addressSchema';
import { flattenYupErrors } from '@validators/yupUtils';

import type { FormType, WizardStep } from '../types/Wizard.types';

interface UseWizardValidationProps {
  steps: readonly WizardStep[];
  currentStep: number;
  values: FormType;
  stripeError?: string;
  // Minor orders must grant the parent/guardian consent to continue.
  isMinorOrder?: boolean;
  // Preventive-testing attestation; omit for family-cascade campaigns like BridgeBio.
  includeFamilyHistoryAttest?: boolean;
}

interface UseWizardValidationReturn {
  isCurrentStepComplete: boolean;
  currentStepErrors: Record<string, string>;
}

/**
 * Hook that validates wizard step completion and generates error messages.
 * Handles step-specific validation rules.
 */
export function useWizardValidation({
  steps,
  currentStep,
  values,
  stripeError,
  isMinorOrder = false,
  includeFamilyHistoryAttest = true,
}: UseWizardValidationProps): UseWizardValidationReturn {
  // Create address schemas with appropriate NY exclusion
  const isCurrentStepComplete = useMemo(() => {
    switch (steps[currentStep]) {
      case 'address':
        if (!values.address) return false;
        try {
          addressExcludeNYSchema.validateSync(values.address, { abortEarly: false });
          return true;
        } catch {
          return false;
        }

      case 'payment':
        // If not using shipping address for billing, billing address fields must be filled
        // (even if payment is already confirmed, user might have unchecked the box)
        if (!values.useShippingForBilling) {
          if (!values.billingAddress) return false;
          try {
            billingAddressSchema.validateSync(values.billingAddress, { abortEarly: false });
            return true;
          } catch {
            return false;
          }
        }
        // If using shipping address, payment confirmation is sufficient
        // If already confirmed, step is complete; otherwise can proceed to confirmation
        return true;

      case 'questionnaire': {
        return Object.values(values.questionnaire || {}).every(value => ['YES', 'NO'].includes(value));
      }

      case 'consents':
        return Boolean(
          values.consents?.acceptGeneral &&
            values.consents?.acceptDNAVisit &&
            (!includeFamilyHistoryAttest || values.consents?.familyHistoryAttest) &&
            (!isMinorOrder || values.consents?.acceptMinor),
        );

      default:
        return false;
    }
  }, [currentStep, values, steps, isMinorOrder, includeFamilyHistoryAttest]);

  const currentStepErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    switch (steps[currentStep]) {
      case 'address':
        if (values.address) {
          try {
            addressExcludeNYSchema.validateSync(values.address, { abortEarly: false });
          } catch (error) {
            if (error instanceof Yup.ValidationError) {
              Object.assign(errs, flattenYupErrors(error, 'address'));
            }
          }
        }
        break;

      case 'payment':
        // Stripe errors are handled separately via stripeError state
        if (stripeError) {
          errs.stripe = stripeError;
        }
        // Billing address errors if not using shipping address
        // Validate even if billingAddress is undefined to generate required field errors
        if (!values.useShippingForBilling) {
          try {
            billingAddressSchema.validateSync(values.billingAddress || {}, { abortEarly: false });
          } catch (error) {
            if (error instanceof Yup.ValidationError) {
              Object.assign(errs, flattenYupErrors(error, 'billingAddress'));
            }
          }
        }
        break;

      case 'questionnaire': {
        Object.entries(values.questionnaire || {}).forEach(([key, value]) => {
          if (!value) {
            errs[`questionnaire.${key}`] = 'Required';
          }
        });
        break;
      }

      case 'consents':
        if (!values.consents?.acceptGeneral) errs['consents.general'] = 'Required';
        if (!values.consents?.acceptDNAVisit) errs['consents.dnavisit'] = 'Required';
        if (includeFamilyHistoryAttest && !values.consents?.familyHistoryAttest) errs['consents.family'] = 'Required';
        if (isMinorOrder && !values.consents?.acceptMinor) errs['consents.minor'] = 'Required';
        break;
    }
    return errs;
  }, [currentStep, values, steps, stripeError, isMinorOrder, includeFamilyHistoryAttest]);

  return {
    isCurrentStepComplete,
    currentStepErrors,
  };
}
